import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import StepIcon, {
  IStepIconProps,
  StepIconModes,
  stepIconSizeRatios,
  StepIconSizes,
} from 'components/Utils/IconsAndImages/StepIcon';
import { useContext } from 'react';

/**
 * A step of a wizard. The KEY is what everything else addresses: step content, validation and
 * navigation all look at the key, so inserting or removing a step is one edit instead of renumbering
 * every index that happened to mean something.
 */
export interface IWizardStep {
  key: string;
  label: string;
  /** A short line under the label, such as the date the step was done. */
  sublabel?: string;
}

interface IWizardProgressProps {
  steps: IWizardStep[];
  activeStepKey: string;
  /**
   * The narrow-screen alternative ("Step 2 of 5 - Team"): a row of labelled icons does not fit on a
   * phone. The caller supplies the finished sentence, because only it knows which locale namespace the
   * wording belongs to. Leaving it out keeps the icons at every width, which is what an unlabelled
   * progress row (a strip of dots) wants. With `alwaysCompact` it is the only thing rendered.
   */
  compactLabel?: string;
  /**
   * Show the compact label at every width instead of the icon row. For a wizard whose steps are DATA -
   * one per item to walk through, which can be dozens - the icon row does not fit on any screen, and a
   * strip of unreadable dots says less than "3 of 27". Needs `compactLabel`.
   */
  alwaysCompact?: boolean;
  /** Every step is done, so none is active. */
  allDone?: boolean;
  /** The colour of a done step: its circle, its sub label and the line to it. The theme colour when absent. */
  doneColor?: string;
}

export const WizardProgress = (props: IWizardProgressProps) => {
  const appContext = useContext(AppContext);

  if (props.steps.length === 0) return null;

  if (props.compactLabel && (props.alwaysCompact || appContext.isMobileView)) {
    return <IPText>{props.compactLabel}</IPText>;
  }

  const activeIndex = props.allDone
    ? props.steps.length
    : props.steps.findIndex((step) => step.key === props.activeStepKey);
  const icons: IStepIconProps[] = props.steps.map((step, index) => ({
    mode: index < activeIndex ? StepIconModes.Success : index === activeIndex ? StepIconModes.Active : StepIconModes.NotReached,
    index: index + 1,
    size: StepIconSizes.Medium,
    label: step.label,
  }));

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'row',
        padding: 10,
      }}
    >
      {props.steps.map((step, index) => {
        const icon = icons[index];

        return (
          <div
            key={step.key}
            style={{
              flex: 1,
              position: 'relative',
            }}
          >
            {index !== 0 && (
              <StepConnector
                stepSize={14 * stepIconSizeRatios[icon.size]}
                color={index <= activeIndex ? props.doneColor : undefined}
              />
            )}
            <StepIcon
              mode={icon.mode}
              index={icon.index}
              size={icon.size}
              label={icon.label}
              color={icon.mode === StepIconModes.Success ? props.doneColor : undefined}
            />
            {step.sublabel && (
              <IPText
                block
                variant="small"
                styles={{
                  root: {
                    textAlign: 'center',
                    color: icon.mode === StepIconModes.Success ? props.doneColor : undefined,
                  },
                }}
              >
                {step.sublabel}
              </IPText>
            )}
          </div>
        );
      })}
    </div>
  );
};



//
// Connector component
//
interface IStepConnectorProps {
  stepSize: number;
  size?: string;
  color?: string;
}

const StepConnector = (props: IStepConnectorProps) => {
  const stepSize = `${props.stepSize}px`;
  const size = `${props.size ?? 1}px`;
  const disabledColor = '#bdbdbd';
  const padding = '-2px';

  return (
    <div
      style={{
        top: `calc((${stepSize} - ${size}) / 2)`,
        left: `calc(-50% + ${stepSize} - ${padding})`,
        right: `calc(50% + ${stepSize} - ${padding})`,
        position: 'absolute',
      }}
    >
      <span
        style={{
          borderTopStyle: 'solid',
          borderTopWidth: size,
          borderColor: props.color ?? disabledColor,
          display: 'block',
        }}
      />
    </div>
  );
};
